import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Bell, LogOut, User, Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const linkCls = ({ isActive }) =>
    `px-4 py-2 rounded-full text-sm font-semibold ${isActive ? "bg-[color:var(--ink)] text-[color:var(--cream)]" : "hover:bg-[color:var(--cream-2)]"}`;
  const mobileLinkCls = ({ isActive }) =>
    `block px-4 py-3 rounded-xl text-base font-semibold ${isActive ? "bg-[color:var(--ink)] text-[color:var(--cream)]" : "hover:bg-[color:var(--cream-2)]"}`;

  const close = () => setOpen(false);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[color:var(--border)]">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-10 py-4">
        <a href="https://hoodriveradoptadog.org" target="_blank" rel="noopener noreferrer" data-testid="nav-home-link" onClick={close} className="flex items-center gap-2">
          <img src="/logo.png" alt="Hood River Adopt A Dog" className="h-16 w-auto" />
          <span className="sr-only">Hood River Adopt A Dog</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-2">
          <NavLink to="/shifts" data-testid="nav-shifts-link" className={linkCls}>Open Shifts</NavLink>
          <NavLink to="/handbook" data-testid="nav-handbook-link" className={linkCls}>Handbook</NavLink>
          {user && (
            <>
              <NavLink to="/dashboard" data-testid="nav-dashboard-link" className={linkCls}>My Dashboard</NavLink>
              <NavLink to="/waiver" data-testid="nav-waiver-link" className={linkCls}>Waiver</NavLink>
              {user.role === "admin" && (
                <NavLink to="/admin" data-testid="nav-admin-link" className={({ isActive }) => `px-4 py-2 rounded-full text-sm font-semibold ${isActive ? "bg-[color:var(--red)] text-white" : "text-[color:var(--red)] hover:bg-[color:var(--red)]/10"}`}>Admin</NavLink>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              <button data-testid="nav-notifications-btn" onClick={() => nav("/dashboard#notifications")} className="p-2 rounded-full hover:bg-[color:var(--cream-2)]" aria-label="Notifications">
                <Bell className="h-5 w-5" />
              </button>
              <Link to="/profile" data-testid="nav-profile-link" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[color:var(--cream-2)] hover:bg-[color:var(--yellow)]/40 transition-colors">
                <User className="h-4 w-4" />
                <span className="text-sm font-semibold" data-testid="nav-user-name">{user.name}</span>
              </Link>
              <button data-testid="nav-logout-btn" onClick={async () => { await logout(); nav("/"); }} className="hidden md:inline-flex btn-pill btn-ghost-ink text-sm">
                <LogOut className="h-4 w-4 inline mr-1" /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline-flex btn-pill btn-ghost-ink text-sm">Sign in</Link>
              <Link to="/register" data-testid="nav-register-btn" className="hidden sm:inline-flex btn-pill btn-primary text-sm">Volunteer</Link>
            </>
          )}
          {/* Hamburger — mobile only */}
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 rounded-full hover:bg-[color:var(--cream-2)]"
            aria-label="Open menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[color:var(--border)] bg-white" data-testid="nav-mobile-drawer">
          <nav className="max-w-7xl mx-auto px-5 py-3 space-y-1">
            <NavLink to="/shifts" onClick={close} data-testid="nav-shifts-link-m" className={mobileLinkCls}>Open Shifts</NavLink>
            <NavLink to="/handbook" onClick={close} data-testid="nav-handbook-link-m" className={mobileLinkCls}>Handbook</NavLink>
            {user && (
              <>
                <NavLink to="/dashboard" onClick={close} data-testid="nav-dashboard-link-m" className={mobileLinkCls}>My Dashboard</NavLink>
                <NavLink to="/waiver" onClick={close} data-testid="nav-waiver-link-m" className={mobileLinkCls}>Waiver</NavLink>
                <NavLink to="/profile" onClick={close} data-testid="nav-profile-link-m" className={mobileLinkCls}>My Profile</NavLink>
                {user.role === "admin" && (
                  <NavLink to="/admin" onClick={close} data-testid="nav-admin-link-m" className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-semibold ${isActive ? "bg-[color:var(--red)] text-white" : "text-[color:var(--red)] hover:bg-[color:var(--red)]/10"}`}>Admin</NavLink>
                )}
                <button
                  data-testid="nav-logout-btn-m"
                  onClick={async () => { close(); await logout(); nav("/"); }}
                  className="w-full text-left block px-4 py-3 rounded-xl text-base font-semibold text-[color:var(--red)] hover:bg-[color:var(--red)]/10"
                >
                  Sign out
                </button>
              </>
            )}
            {!user && (
              <>
                <NavLink to="/login" onClick={close} data-testid="nav-login-btn-m" className={mobileLinkCls}>Sign in</NavLink>
                <NavLink to="/register" onClick={close} data-testid="nav-register-btn-m" className={({ isActive }) => `block px-4 py-3 rounded-xl text-base font-semibold ${isActive ? "bg-[color:var(--red)] text-white" : "bg-[color:var(--red)] text-white hover:opacity-90"}`}>Volunteer</NavLink>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
